import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

interface TableData {
    id?: string;
    name: string;
    capacity: number;
    shape: 'round' | 'rectangle' | 'long';
    sortOrder: number;
}

interface AddTableModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editTable?: TableData | null;
    nextSortOrder?: number;
}

export const AddTableModal: React.FC<AddTableModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    editTable = null,
    nextSortOrder = 0,
}) => {
    const [name, setName] = useState('');
    const [capacity, setCapacity] = useState(8);
    const [shape, setShape] = useState<'round' | 'rectangle' | 'long'>('round');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isEditing = !!editTable;

    useEffect(() => {
        if (editTable) {
            setName(editTable.name);
            setCapacity(editTable.capacity);
            setShape(editTable.shape);
        } else {
            setName('');
            setCapacity(8);
            setShape('round');
        }
        setError('');
    }, [editTable, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            setError('Table name is required');
            return;
        }
        if (capacity < 1 || capacity > 30) {
            setError('Capacity must be between 1 and 30');
            return;
        }

        setLoading(true);
        setError('');

        try {
            if (isEditing && editTable?.id) {
                await updateDoc(doc(db, 'tables', editTable.id), {
                    name: name.trim(),
                    capacity,
                    shape,
                    updatedAt: new Date(),
                });
            } else {
                await addDoc(collection(db, 'tables'), {
                    name: name.trim(),
                    capacity,
                    shape,
                    sortOrder: nextSortOrder,
                    seatOrder: [],
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
            }
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save table');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!editTable?.id) return;
        if (!window.confirm(`Delete "${editTable.name}"? Guests assigned to this table will NOT be unassigned — you'll need to reassign them manually.`)) return;

        setLoading(true);
        try {
            await deleteDoc(doc(db, 'tables', editTable.id));
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to delete table');
        } finally {
            setLoading(false);
        }
    };

    const shapes = [
        { value: 'round' as const, label: 'Round', icon: '○' },
        { value: 'rectangle' as const, label: 'Rectangle', icon: '□' },
        { value: 'long' as const, label: 'Long / Head', icon: '▬' },
    ];

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Table' : 'Add Table'}>
            <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                    <div className="p-3 bg-error/10 border border-error/20 rounded-xl text-error text-sm font-medium">
                        {error}
                    </div>
                )}

                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                        Table Name
                    </label>
                    <input
                        type="text"
                        className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. Mesa 1, Head Table"
                        autoFocus
                    />
                </div>

                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                        Capacity
                    </label>
                    <input
                        type="number"
                        className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                        value={capacity}
                        onChange={e => setCapacity(parseInt(e.target.value) || 0)}
                        min={1}
                        max={30}
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                        Shape
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                        {shapes.map(s => (
                            <button
                                key={s.value}
                                type="button"
                                onClick={() => setShape(s.value)}
                                className={`p-3 rounded-xl border-2 transition-all text-center ${
                                    shape === s.value
                                        ? 'border-primary bg-primary/5 text-secondary'
                                        : 'border-gray-200 bg-white text-secondary-light hover:border-gray-300'
                                }`}
                            >
                                <span className="text-2xl block mb-1">{s.icon}</span>
                                <span className="text-xs font-bold uppercase tracking-wider">{s.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex gap-3 pt-2">
                    <Button type="submit" size="sm" disabled={loading} className="flex-1">
                        {loading ? 'Saving...' : isEditing ? 'Update Table' : 'Add Table'}
                    </Button>
                    {isEditing && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleDelete}
                            disabled={loading}
                            className="text-error hover:bg-error/10 hover:border-error/20"
                        >
                            Delete
                        </Button>
                    )}
                </div>
            </form>
        </Modal>
    );
};
